import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import Img from '../../../components/Img';
import { Datepicker, Select } from '../../../components/Form';
import { dateFormat } from '../../../utils/courtSchedular';

import styles from './nextAvailable.scss';
import HeaderCount from './HeaderCount';
import { clearPublishStatus, publishDrawsForFacilityAndDayConfig, setSelectedFacility } from '../../../actions/next-available-actions';
import useHeaderDateFilter from '../SchedularTypeComponents/useHeaderDateFilter';
import NextAvailableSidePanel from './NextAvailableSidePanel';
import { generatePublishPreviewDay, isDrawPublished } from '../../../utils/nextAvailable';
import NextAvailableScheduleDay from '../../EventProfiles/EventProfilePageV3/PageComponents/ScheduleTabContent/NextAvailableScheduleDay';

const NextAvailableHeader = (props) => {
    const {
        event,
        onClickRefresh,
        onClickSettings,
        onChangeDate,
    } = props;

    const [showDrawPublishPreview, setShowDrawPublishPreview] = useState();
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);
    const dispatch = useDispatch();
    const {
        selectedDate,
        facilitiesAvailable,
        selectedFacilityId,
        summary,
        publishStatus = {},
    } = nextAvailable;

    const {
        calendarSelectedDate,
        calendarStartDate,
        calendarEndDate,
        onFacilitySelected,
        onDateChange,
        onGotoPreviousDayClick,
        onGotoNextDayClick,
    } = useHeaderDateFilter({
        event,
        onChangeDate,
        selectedDate,
        facilitiesAvailable,
        setSelectedFacility,
    });

    const areDrawsAvailable = nextAvailable.draws?.length > 0;
    const areDrawsAvailableToPublish = nextAvailable.draws?.filter((draw) => {
        return !isDrawPublished(draw);
    }).length > 0;

    const onPublishClick = () => {
        setShowDrawPublishPreview(true);
    };

    const onConfirmPublish = () => {
        dispatch(publishDrawsForFacilityAndDayConfig(event?.id));
    };

    const onCancelPublish = () => {
        setShowDrawPublishPreview(false);
    };

    useEffect(() => {
        let interval;
        if (publishStatus.isPublishSuccess) {
            setShowDrawPublishPreview(false);
            interval = setInterval(() => {
                dispatch(clearPublishStatus());
            }, 5000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [publishStatus.isPublishSuccess]);

    return (
        <>
            <div className={styles.courtGridHeader}>
                <button
                    className={`btn ${styles.dateSlider}`}
                    type="button"
                    onClick={onGotoPreviousDayClick}
                    disabled={calendarSelectedDate.isSame(calendarStartDate)}
                    data-testid="next-available.header.prev-day-btn"
                >
                    <Img src="/img/v3/icons/icon-24px-arrow-left.svg" />
                </button>
                <Datepicker
                    className={`${styles.optionSelect} ${styles.dateSelect}`}
                    selected={calendarSelectedDate}
                    format={dateFormat}
                    minDate={calendarStartDate}
                    maxDate={calendarEndDate}
                    themeV3
                    onChange={onDateChange}
                    data-testid="next-available.header.date-picker"
                />
                <button
                    className={`btn ${styles.dateSlider}`}
                    type="button"
                    onClick={onGotoNextDayClick}
                    disabled={calendarSelectedDate.isSame(calendarEndDate)}
                    data-testid="next-available.header.next-day-btn"
                >
                    <Img src="/img/v3/icons/icon-24px-arrow-right.svg" />
                </button>
                <Select
                    attributeToShow="name"
                    attributeToSave="id"
                    themeV3
                    emptyOption="Select Facility"
                    options={facilitiesAvailable}
                    formGroupCssClass={styles.optionSelect}
                    controlled
                    hiddenDefault={facilitiesAvailable.length > 0}
                    value={selectedFacilityId}
                    onChange={onFacilitySelected}
                    disabled={facilitiesAvailable.length < 2}
                    data-testid="next-available.header.facility-select"
                />
                <div className={styles.divider} />
                <HeaderCount label="Draws" value={summary.draws} data-testid="next-available.header.count-draws" />
                <div className={styles.divider} />
                <HeaderCount label="On" value={summary.matchesOnCourt} data-testid="next-available.header.count-on" />
                <HeaderCount label="To do" value={summary.matchesTodo} data-testid="next-available.header.count-todo" />
                <HeaderCount label="Queue" value={summary.matchesInQueue} data-testid="next-available.header.count-queue" />
                <HeaderCount label="Done" value={summary.matchesDone} data-testid="next-available.header.count-done" />
                <HeaderCount label="Total" value={summary.matches} data-testid="next-available.header.count-total" />
                <div className="flex-1" />
                <button
                    className={`btn btn-flex p0`}
                    type="button"
                    onClick={onClickRefresh}
                    data-testid="next-available.header.refresh-btn"
                >
                    <Img src="/img/v3/icons/icon-rollover.svg" className={styles.refresh} />
                </button>
                <button
                    className={`btn btn-flex ${styles.settings} p0`}
                    type="button"
                    onClick={onClickSettings}
                    data-testid="next-available.header.settings-btn"
                >
                    <Img src="/img/v3/icons/icon-24px-settings-blue.svg" />
                </button>
                {areDrawsAvailable && publishStatus.isPublishSuccess && (
                    <button
                        className={`btn btn-flex ${styles.status} p0`}
                        type="button"
                        data-testid="next-available.header.published-btn"
                    >
                        <i className={`far fa-regular fa-check ${styles.statusIcon}`} />Published
                    </button>
                )}
                {areDrawsAvailable && publishStatus.isPublishing && (
                    <button
                        className={`btn btn-flex ${styles.status} p0`}
                        type="button"
                        data-testid="next-available.header.publishing-btn"
                    >
                        <i className={`far fa-regular fa-loader ${styles.statusIcon}`} />Publishing
                    </button>
                )}
                {areDrawsAvailable && areDrawsAvailableToPublish && !publishStatus.isPublishSuccess && !publishStatus.isPublishing && (
                    <button
                        className={`btn btn-primary ${styles.publish}`}
                        type="button"
                        onClick={onPublishClick}
                        data-testid="next-available.header.publish-btn"
                    >
                        Publish
                    </button>
                )}
            </div>
            {showDrawPublishPreview && (
                <NextAvailableSidePanel
                    className={styles.publishPreview}
                    fullScreen
                >
                    <div className={styles.header} data-testid="next-available.header.publish-preview.title">
                        Schedule publish preview
                    </div>
                    <div className={styles.content}>
                        <NextAvailableScheduleDay
                            className="p0"
                            scheduleDay={generatePublishPreviewDay(nextAvailable)}
                            eventSchedule={event?.eventSchedule}
                        />
                    </div>
                    <div className={`${styles.footer} d-flex align-items-center`}>
                        <button
                            className={`btn btn-default ${styles.submitButton}`}
                            type="button"
                            onClick={onCancelPublish}
                            disabled={publishStatus.isPublishing}
                            data-testid="next-available.header.publish-preview.cancel-btn"
                        >
                            Cancel
                        </button>
                        <div className="flex-1" />
                        <button
                            className={`btn btn-primary ${styles.submitButton}`}
                            type="submit"
                            onClick={onConfirmPublish}
                            disabled={publishStatus.isPublishing}
                            data-testid="next-available.header.publish-preview.confirm-btn"
                        >
                            Publish
                        </button>
                    </div>
                </NextAvailableSidePanel>
            )}
        </>
    );
};

NextAvailableHeader.propTypes = {
    event: PropTypes.objectOf(any).isRequired,
    onClickSettings: PropTypes.func.isRequired,
    onChangeDate: PropTypes.func.isRequired,
    onClickRefresh: PropTypes.func.isRequired,
};

NextAvailableHeader.defaultProps = {};

export default NextAvailableHeader;
